import { useCallback, useMemo, useState } from 'react';

import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';

const DEFAULT_PAGE_SIZE = 10;

export const MTR_PICKER_BASE_FILTERS = {
  status: 'ACTIVE',
  mtrKind: 'NORMAL',
};

/**
 * 選料 Dialog 本地 listQuery（不寫 URL）。
 * status 固定 ACTIVE；includeMtrKind 預設 true 時再鎖 mtrKind=NORMAL。
 * extraFilters（如 manufacturerId）鎖定不可由 UI 清除。
 */
function useMtrPickerListQuery({
  extraFilters = {},
  includeMtrKind = true,
} = {}) {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [keyword, setKeywordState] = useState('');
  const [sort, setSortState] = useState([]);
  const [userFilters, setUserFilters] = useState({});

  const baseFilters = useMemo(() => {
    if (includeMtrKind) {
      return MTR_PICKER_BASE_FILTERS;
    }
    return { status: 'ACTIVE' };
  }, [includeMtrKind]);

  const lockedFilterKeys = useMemo(() => (
    new Set([...Object.keys(baseFilters), ...Object.keys(extraFilters ?? {})])
  ), [baseFilters, extraFilters]);

  const listQuery = useMemo(() => ({
    page,
    pageSize,
    keyword,
    sort,
    filters: {
      ...baseFilters,
      ...extraFilters,
      ...userFilters,
    },
  }), [page, pageSize, keyword, sort, userFilters, baseFilters, extraFilters]);

  const setPage = useCallback((nextPage) => {
    setPageState(nextPage);
  }, []);

  const setPageSize = useCallback((nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPageState(1);
  }, []);

  const setKeyword = useCallback((nextKeyword) => {
    setKeywordState(nextKeyword ?? '');
    setPageState(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    if (lockedFilterKeys.has(key)) {
      return;
    }

    setUserFilters((prev) => mergeFilterUpdate(prev, key, value));
    setPageState(1);
  }, [lockedFilterKeys]);

  const setSort = useCallback((nextSort) => {
    setSortState(nextSort ?? []);
    setPageState(1);
  }, []);

  const removeSortAt = useCallback((index) => {
    setSortState((prev) => (prev ?? []).filter((_, sortIndex) => sortIndex !== index));
    setPageState(1);
  }, []);

  const resetQuery = useCallback(() => {
    setPageState(1);
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setKeywordState('');
    setSortState([]);
    setUserFilters({});
  }, []);

  return {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  };
}

export default useMtrPickerListQuery;
